import { ImmunizationRecord, ImmunizationSeries } from "../models/Interfaces";
import React, { useEffect, useRef } from "react";
import { infoLog } from "../utils/logger";
import { useTranslation } from "react-i18next";
import "./recordCard.css";
import { useNavigate } from "react-router";
import getIzProvider, { IzProvider } from "../models/providers/IzProviders";
import { getStatusColor, getStatusLabel } from "../utils/status";
import { useAtom } from "jotai";
import { izProviderConfigsAtom, isHydrated } from "../jotai/atoms";
import { getDateTime } from "../utils/data";
import { DateTime } from "luxon";
import { APP_DOMAIN, PROTOCOL } from "../globals";
import { Browser } from "@capacitor/browser";
import { Capacitor } from "@capacitor/core";
import { ModalV2, ModalV2Header, ModalV2Body, ModalV2BackButton } from "./modals/ModalV2";
import EventCard from "./EventCard";
import { useModal } from "../context/ModalContext";

interface RecordCardProps {
  index: number;
  record: ImmunizationRecord;
  series: ImmunizationSeries;
  onSelect: Function;
}

// Virus images
const adenovirus = "/assets/images/Adenovirus.png";
const anthrax = "/assets/images/Anthrax.png";
const cholera = "/assets/images/Cholera.png";
const covid = "/assets/images/Covid19.png";
const dtap = "/assets/images/DTap.png";
const generic = "/assets/images/Default.png";
const hepa = "/assets/images/HepatitisA.png";
const hepb = "/assets/images/HepatitisB.png";
const hib = "/assets/images/HIB.png";
const hpv = "/assets/images/HumanPapilloma.png";
const influenza = "/assets/images/Influenza.png";
const japanese_encephalitis = "/assets/images/Japanese_Encephalitis.png";
const meningococcal = "/assets/images/Meningococcal.png";
const mmr = "/assets/images/MMR.png";
const monkeypox = "/assets/images/monkeypox.png";
const pneumococcal = "/assets/images/Pneumococcal.png";
const polio = "/assets/images/Polio.png";
const rabies = "/assets/images/rabies.png";
const rotavirus = "/assets/images/Rotavirus.png";
const rsv = "/assets/images/RSV.png";
const tb = "/assets/images/Tuberculosis.png";
const typhoid = "/assets/images/typhoid.png";
const varicella = "/assets/images/Varicella.png";
const yellow_fever = "/assets/images/yellow_fever.png";
const zoster = "/assets/images/zoster.png";

const update = "/assets/images/njUpdate2.png";

// TODO: i18n - We should make this part of the i18n files and just swap their paths
const overdue_es = "/assets/images/atrasada.png";
const record_es = "/assets/images/record_es.png";
const complete_es = "/assets/images/completa.png";
const current_es = "/assets/images/aldia.png";
const immune_es = "/assets/images/immuneStatusEs.png";
const dueNow_es = "/assets/images/letocaahora.png";

const qr_blue = "/assets/images/Icon_QR_Code_Blue.png";
const qr_red = "/assets/images/Icon_QR_Code_Red.png";
const qr_green = "/assets/images/Icon_QR_Code_Green.png";
const qr_yellow = "/assets/images/Icon_QR_Code_Yeller.png";
const qr_blue_hc = "/assets/images/Icon_QR_Code_Blue_HC.png";
const qr_red_hc = "/assets/images/Icon_QR_Code_Red_HC.png";
const qr_green_hc = "/assets/images/Icon_QR_Code_Green_HC.png";
const qr_yellow_hc = "/assets/images/Icon_QR_Code_Yeller_HC.png";

const calendar_red = "/assets/images/Icon_Calendar_DUE.png";
const calendar_yellow = "/assets/images/Icon_Calendar_DUE-NOW.png";
const calendar_green = "/assets/images/Icon_Calendar_CURRENT.png";
const calendar_red_dark = "/assets/images/Icon_Calendar_DUE_Dark.png";
const calendar_yellow_dark = "/assets/images/Icon_Calendar_DUE-NOW_Dark.png";
const calendar_green_dark = "/assets/images/Icon_Calendar_CURRENT_Dark.png";

export default function RecordCard({ record, series, index, onSelect }: RecordCardProps) {
  const [providerConfigs] = useAtom(izProviderConfigsAtom);

  const navigate = useNavigate();
  const modal = useModal();
  const { t, i18n } = useTranslation();
  const status = series.status.toLowerCase();
  const provider = record.izProviderKey.toLowerCase();
  const izProvider = getIzProvider(record.izProviderKey, t);
  const seriesDate = series.dateDue ? series.dateDue : series.earliestDateToGive;
  const modalRef = useRef<HTMLDialogElement>(null);

  const providerEnabledQrCode = () => {
    if (!isHydrated(providerConfigs)) {
      return false;
    }
    const matchingProviderConfigs = providerConfigs.filter(
      (pc) => pc.iz_provider.destination_id.toLowerCase() === record.izProviderKey.toLowerCase()
    );
    if (matchingProviderConfigs.length > 1) {
      throw new Error(
        `Expected to only match 1 IZ provider when determining whether or not to show a QR code, found ${matchingProviderConfigs.length}`
      );
    }

    if (matchingProviderConfigs.length === 0) {
      // We can't find this provider, so disable it by default
      return false;
    }

    const config = matchingProviderConfigs[0];
    return config.features && config.features.qr_code;
  };

  const supportsCovidQRCode = () => {
    return (
      series.vaccineType.toLowerCase().includes("covid") &&
      series.events.length > 0 &&
      providerEnabledQrCode()
    );
  };

  const getShotLabel = () => {
    const shotCount = series.events.length;
    return provider === "ak"
      ? `${t("immunizations.records_doses", { count: shotCount })}`
      : `${t("immunizations.records_shots", { count: shotCount })}`;
  };

  const getVirusImage = () => {
    let virus = series.vaccineType.toLowerCase();
    if (virus === "hep-a" || virus === "hep a" || virus === "hepa" || virus === "hepatitis a") {
      return hepa;
    } else if (
      virus === "hep-b" ||
      virus === "hep b" ||
      virus === "hepb" ||
      virus === "hepatitis b"
    ) {
      return hepb;
    } else if (
      virus.includes("easles") ||
      virus.includes("mumps") ||
      virus.includes("rubella") ||
      virus.includes("mmr")
    ) {
      return mmr;
    } else if (virus.toLowerCase().includes("covid")) {
      return covid;
    } else if (virus === "hib" || virus === "haemophilus influenzae b") {
      return hib;
    } else if (virus.includes("monkey") || virus.includes("small")) {
      //or smallpox
      return monkeypox;
    } else if (virus.includes("pox") || virus.includes("var")) {
      //chickenpox
      //top = -3.8
      return varicella;
    } else if (virus.includes("flu")) {
      //top = -3.5
      return influenza;
    } else if (
      virus.includes("td") ||
      virus.includes("dtp") ||
      virus.includes("tap") ||
      virus === "diphtheria, tetanus, pertussis"
    ) {
      return dtap;
    } else if (virus.includes("gococcal") || virus.includes("mening")) {
      return meningococcal;
    } else if (virus.includes("neumo")) {
      return pneumococcal;
    } else if (virus.includes("roto") || virus.includes("rota")) {
      return rotavirus;
    } else if (virus.includes("polio")) {
      return polio;
    } else if (virus.includes("COVID") || virus.includes("-19")) {
      return covid;
    } else if (virus.includes("hpv")) {
      return hpv;
    } else if (virus.includes("zoster")) {
      return zoster;
    } else if (virus.includes("typhoid")) {
      return typhoid;
    } else if (virus.includes("rabies")) {
      return rabies;
    } else if (virus.includes("encephalitis")) {
      return japanese_encephalitis;
    } else if (virus.includes("yellow")) {
      return yellow_fever;
    } else if (
      virus.toLowerCase().includes("tuberculosis") ||
      virus.toLowerCase().includes("bsv")
    ) {
      return tb;
    } else if (
      virus.toLowerCase().includes("rsv") ||
      virus.toLowerCase().includes("respiratory syncytial virus")
    ) {
      return rsv;
    } else if (virus.toLowerCase().includes("cholera")) {
      return cholera;
    } else if (virus.toLowerCase().includes("adenovirus")) {
      return adenovirus;
    } else if (virus.toLowerCase().includes("anthrax")) {
      return anthrax;
    }

    return generic;
  };

  const getQrImage = (highContrast: boolean) => {
    let img = highContrast ? qr_blue_hc : qr_blue;
    if (status === "overdue" || (status === "due_now" && izProvider.statusImages.redOnDue)) {
      img = highContrast ? qr_red_hc : qr_red;
    } else if (status === "due_now") {
      img = highContrast ? qr_yellow_hc : qr_yellow;
    } else if (status === "current" || status === "complete") {
      img = highContrast ? qr_green_hc : qr_green;
    }
    return img;
  };

  const getCalendarImage = (dark: boolean) => {
    const status = series.status.toLowerCase();

    if (status === "overdue") {
      return dark ? calendar_red_dark : calendar_red;
    } else if (status === "due_now") {
      return dark ? calendar_yellow_dark : calendar_yellow;
    } else if (status === "current") {
      return dark ? calendar_green_dark : calendar_green;
    }

    return "";
  };

  const getRecordedInText = () => {
    // This exception exists here since we have to embed the QR code
    // next to COVID, which was pushing text out too much on the mobile app.
    // This may not be needed since our UI is different in the PWA.
    let msg = t("immunizations.recorded_in", { iisAbbrev: izProvider.iisAbbrev });
    if (supportsCovidQRCode()) {
      msg = ` In ${izProvider.iisAbbrev}`;
    }
    return msg;
  };

  const readableDate = () => {
    const date = series.dateDue ? series.dateDue : series.earliestDateToGive;
    if (
      date &&
      (status === "overdue" || status === "due_now" || status === "due" || status === "current")
    ) {
      return getDateTime(date).toLocaleString();
    }
    return t("immunizations.records_no_due_date");
  };

  const renderDueDateInfo = () => {
    if (series.dateDue && series.dateOverdue) {
      return (
        <>
          <span className="text-info-content mr-2 font-bold dark:text-secondary-content">
            {t("immunizations.records_due_on")}
          </span>
          <span className="font-semibold">
            {getDateTime(series.dateDue).toLocaleString()} -{" "}
            {getDateTime(series.dateOverdue).toLocaleString()}
          </span>
        </>
      );
    }
    if (series.dateOverdue) {
      return (
        <>
          <span className="text-info-content mr-2 font-bold dark:text-secondary-content">
            {t("immunizations.records_overdue_after")}
          </span>
          <span className="font-semibold">{getDateTime(series.dateOverdue).toLocaleString()}</span>
        </>
      );
    }
    if (series.dateDue) {
      return (
        <>
          <span className="text-info-content mr-2 font-bold dark:text-secondary-content">
            {t("immunizations.records_due_on_or_after")}
          </span>
          <span className="font-semibold">{getDateTime(series.dateDue).toLocaleString()}</span>
        </>
      );
    }
    return (
      <>
        <span className="text-info-content mr-2 font-bold dark:text-secondary-content">
          {t("immunizations.records_due_on")}
        </span>
        <span className="font-semibold">{t("immunizations.records_no_due_date")}</span>
      </>
    );
  };

  const hasFutureDate = () => {
    if (!seriesDate) {
      return false;
    }

    return getDateTime(seriesDate) > DateTime.now();
  };

  const getTitleText = (text: string, shorten = true) => {
    let newText = t("immunizations.records_processing");
    if (text && text !== "wait") {
      if (!shorten) {
        return text;
      }
      newText = text.length > 23 ? text.substring(0, 20) + "..." : text;
    }
    return newText;
  };

  const goToSmartHealthCard = () => {
    modal.openSmartHealthCardModal(record.uid);
  };

  const addToCalendar = () => {
    if (!seriesDate) return false;

    const eventTitle = "Immunization Reminder";
    const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
    const eventDescription =
      "Docket® Immunization Reminder: Please check your Docket® account for more information about your recommended immunization(s). Be sure to consult a healthcare provider before getting a new vaccine.";

    const seriesDateTime = getDateTime(seriesDate);
    const currDate = DateTime.now().toFormat("yyyyLLdd'T'HHmmss");
    const eventStart = seriesDateTime.toFormat("yyyyLLdd");
    const eventEnd = seriesDateTime.plus({ days: 1 }).toFormat("yyyyLLdd");
    const uniqueId = `${seriesDateTime.toFormat("yyyyLLdd'T'HHmmss")}-${currDate}`;

    const icsData = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "CALSCALE:GREGORIAN",
      "METHOD:PUBLISH",
      "PRODID:-//Docket//Docket Immunization Reminder//EN",
      `X-WR-TIMEZONE:${timeZone}`,
      "BEGIN:VEVENT",
      `DTSTART;VALUE=DATE:${eventStart}`,
      `DTEND;VALUE=DATE:${eventEnd}`,
      `DTSTAMP:${currDate}`,
      `UID:${uniqueId}`,
      `CREATED:${currDate}`,
      `DESCRIPTION:${eventDescription}`,
      `SUMMARY:${eventTitle}`,
      "TRANSP:TRANSPARENT",
      "BEGIN:VALARM",
      "ACTION:DISPLAY",
      "DESCRIPTION:Reminder",
      "TRIGGER;RELATED=START:-PT0M",
      "END:VALARM",
      "END:VEVENT",
      "END:VCALENDAR",
    ].join("\r\n");

    const calendarLink = `data:text/calendar,${encodeURIComponent(icsData)}`;
    if (Capacitor.isNativePlatform()) {
      Browser.open({ url: `${PROTOCOL}://${APP_DOMAIN}/add-event?link=${calendarLink}` });
    } else {
      window.open(`/add-event?link=${calendarLink}`, "_blank", "rel=noopener noreferrer");
    }
  };

  const statusLabel = getStatusLabel(izProvider, status, series);

  return (
    <>
      <ModalV2 ref={modalRef} modalBoxClassName="dark:bg-secondary bg-info">
        <div>
          <ModalV2Header className="flex items-center gap-2 py-2">
            <ModalV2BackButton />
            <p className="font-semibold text-xl">
              {record.firstName} {record.lastName}
            </p>
          </ModalV2Header>
          <ModalV2Header
            className={` flex items-center justify-center text-info-content py-4 max-md:h-fit ${getStatusColor(
              izProvider,
              status,
              series
            )} ${statusLabel === "record" ? "text-primary-content" : ""}`}
          >
            <p className={`text-2xl uppercase text-center font-bold `}>
              {t("immunizations.records_status", {
                context: statusLabel,
              })}
            </p>
          </ModalV2Header>
          <ModalV2Header className="bg-info dark:bg-secondary text-info-content dark:text-secondary-content max-md:h-fit">
            <div className="flex flex-col w-full">
              <p className={`text-xl uppercase text-center font-bold pt-2`}>
                {getTitleText(series.vaccineType, false)}
              </p>
              <div className="flex justify-center pb-2 ">{renderDueDateInfo()}</div>
            </div>
          </ModalV2Header>
        </div>
        <ModalV2Body className="pt-2 bg-info dark:bg-secondary">
          {series?.events?.length === 0 && (
            <p className="text-center text-lg font-bold text-info-content dark:text-secondary-content">
              {t("immunizations.records_no_events_message")}
            </p>
          )}
          {series?.events?.map((e, index) => (
            <EventCard key={index} event={e} vaccine={series.vaccineType} />
          ))}
        </ModalV2Body>
      </ModalV2>
      <div onClick={() => modalRef.current?.showModal()} className="cursor-pointer">
        <div className="card card-side bg-base-100 text-base-content overflow-hidden border-0">
          <div className="flex flex-1">
            <div
              className={`sideways-writing text-center uppercase text-neutral text-nowrap py-1 px-2 font-bold ${getStatusColor(
                izProvider,
                status,
                series
              )} ${statusLabel === "record" ? "text-primary-content" : ""}`}
            >
              {t("immunizations.records_status", {
                context: statusLabel,
              })}
            </div>

            <div className="border-2 border-l-0 border-r-0 border-info dark:border-base-100 flex item-center justify-center w-full">
              <div className="card-body py-2">
                <div role="paragraph" className="card-title" data-testid="record-card-title">
                  {getTitleText(series.vaccineType)}
                </div>
                <div className="grid grid-cols-[min-content_1fr]">
                  <span className="text-primary mr-2 font-bold">
                    {t("immunizations.records_due_on")}
                  </span>
                  <span className="font-semibold">{readableDate()}</span>
                  {/* TODO */}
                  <span className="text-primary mr-2 font-bold">Records:</span>
                  <span className="font-semibold">{`${getShotLabel()} ${getRecordedInText()}`}</span>
                </div>
              </div>
              <div className="flex flex-col py-2 mr-2">
                {hasFutureDate() && (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      addToCalendar();
                    }}
                    className="my-auto"
                  >
                    <img
                      className="record-card-right-img dark:hidden"
                      src={getCalendarImage(false)}
                      alt={`Calendar Image`}
                    />
                    <img
                      className="record-card-right-img not-dark:hidden"
                      src={getCalendarImage(true)}
                      alt={`Calendar Image`}
                    />
                  </div>
                )}
                {supportsCovidQRCode() && (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      goToSmartHealthCard();
                    }}
                    className="my-auto"
                  >
                    <img
                      className="record-card-right-img dark:hidden"
                      src={getQrImage(false)}
                      alt="Placeholder image"
                    />
                    <img
                      className="record-card-right-img not-dark:hidden"
                      src={getQrImage(true)}
                      alt="Placeholder image"
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="flex bg-info">
              <div className="my-auto px-2">
                <img
                  className="max-sm:w-20 w-24"
                  src={getVirusImage()}
                  alt={`${series.vaccineType.toLowerCase()} Image`}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
